import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CallIntelMutations, CallIntelTypes } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import { getUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { NakedUl, useAlert, useForm } from '@frontend/design-system';
import { analyzingCallLottie } from '../../assets/lotties';
import { useHasCallIntelAclAccess } from '../../hooks';
import { CallFlagsData, CallInsightBaseProps } from '../../types';
import {
  computeIsFieldEdited,
  FormState,
  getFormStateByCall,
  mapFormStateToCall,
  normalizeEmptyStrings,
} from '../../utils/call-analysis-util';
import { CallEmptyState } from '../empty-state/call-empty-state';
import { AnalyzingCallLoader } from '../loaders/analyzing-call-loader';
import { ActionButtons } from './action-buttons';
import {
  AppointmentTypeFormField,
  CallSentimentFormField,
  CategoryFormField,
  ContactTypeFormField,
  SchedulingOutcomeV2Field,
  ServiceIssuesFormField,
} from './call-analysis-form-field';
import { EditBanner } from './edit-banner';

export interface CallAnalysisProps extends CallInsightBaseProps {
  callFlags: CallFlagsData;
  isEditMode: boolean;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  containerStyles?: Interpolation<Theme>;
  onUpdateCall: (call: CallIntelTypes.CallRecord) => void;
  setUnsavedEditChangesConfig: (config: CallIntelTypes.UnsavedEditChangesConfig) => void;
  setIsEditMode: (isEditMode: boolean) => void;
}

export const CallAnalysis: FC<CallAnalysisProps> = ({
  callFlags,
  call,
  isEditMode,
  isLoading,
  isDemoAccount = false,
  unsavedEditChangesConfig,
  containerStyles,
  onUpdateCall,
  setUnsavedEditChangesConfig,
  setIsEditMode,
}) => {
  const { t } = useTranslation('callIntelInsights');
  const alert = useAlert();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const hasAclAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_EDIT_AI_OUTPUT]);
  const isServiceQualityEnabled = getFeatureFlagValue('enable-service-quality');

  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const initialFormState = useMemo(() => getFormStateByCall(call), [call]);

  const [callFormState, setCallFormState] = useState(initialFormState);
  const [isFormLevelSaveDisabled, setIsFormLevelSaveDisabled] = useState(true);
  const [changedFields, setChangedFields] = useState<(keyof FormState)[] | null>(null);
  const [pendingUpdateCallInList, setPendingUpdateCallInList] = useState<null | Partial<CallIntelTypes.CallRecord>>(
    null
  );
  const [isRestored, setIsRestored] = useState(false);
  const [pendingFieldSave, setPendingFieldSave] = useState<keyof FormState | null>(null);

  const isFormEdited = useMemo(() => {
    if (!call?.isFieldEdited) return false;

    // Build a list of keys to check, excluding isServiceQualityFlagEdited if not enabled
    const keysToCheck = Object.keys(call.isFieldEdited).filter(
      (key) => key !== 'isServiceQualityFlagEdited' || (isServiceQualityEnabled && hasServiceQualityAccess)
    );

    return keysToCheck.some((key) => call.isFieldEdited?.[key as keyof typeof call.isFieldEdited]);
  }, [call, isServiceQualityEnabled, hasServiceQualityAccess]);

  const [correctionBannerConfig, setCorrectionBannerConfig] = useState({
    canMakeCorrection: hasAclAccess,
    isReviewed: isFormEdited,
    showEditHistory: isFormEdited,
  });

  const {
    changedValues: formChangedValues,
    getFieldProps,
    isComplete: isFormComplete,
    reset: resetForm,
    seedValues: seedValues,
    values: formValues,
  } = useForm({
    computeChangedValues: true,
    fields: {
      appointmentTypes: { type: 'checklist', value: callFormState.appointmentTypes },
      categories: { type: 'checklist', value: callFormState.categories, required: true },
      contactType: { type: 'radio', value: callFormState.contactType },
      schedulingOpportunity: {
        type: 'radio',
        value: callFormState.schedulingOpportunity,
      },
      schedulingOutcome: {
        type: 'radio',
        value: callFormState.schedulingOutcome,
      },
      schedulingOutcomeV2: {
        type: 'radio',
        value: callFormState.schedulingOutcomeV2,
      },
      callSentiment: { type: 'radio', value: callFormState.callSentiment },
      serviceQualityFlags: {
        type: 'checklist',
        value: callFormState.serviceQualityFlags,
      },
    },
  });

  const { mutate: editAIOutputMutate, isLoading: isAIOutputMutating } = CallIntelMutations.useEditAiOutput({
    onSuccess: () => {
      setCallFormState(normalizeEmptyStrings(formValues) as typeof callFormState);
      setIsEditMode(false);
      setCorrectionBannerConfig((prev) => ({
        ...prev,
        canMakeCorrection: true,
        isReviewed: true,
        showEditHistory: true,
      }));

      const updatedCallFields = mapFormStateToCall(normalizeEmptyStrings(formValues) as FormState);

      setPendingUpdateCallInList(updatedCallFields);

      alert.success(t('Successfully saved changes!'));
    },
    onError: (err) => {
      console.error(err);
      if (http.isHttpError(err)) {
        const errorMessage =
          ((err.data as Record<string, any>)?.code as number) !== 3
            ? t('Unable to save changes. Please try again.')
            : t('Updates were made by another user. Please refresh and try again.');
        alert.error(errorMessage);
      }
    },
  });

  const { isSkippedCall, isFailedCall, isProcessingCall, isPrivateCall } = callFlags;
  const isSkippedOrFailedCall = isSkippedCall || isFailedCall;
  const showCorrectionBanner = isSkippedOrFailedCall ? false : hasAclAccess || isFormEdited;

  const initiateCorrectionMode = () => {
    setIsEditMode(true);
    setCorrectionBannerConfig((prev) => ({ ...prev, canMakeCorrection: false }));
    resetForm();
  };

  const cancelEditMode = () => {
    seedValues(callFormState);
    setIsEditMode(false);
    setCorrectionBannerConfig((prev) => ({ ...prev, canMakeCorrection: true }));
  };

  const computeChangedPayload = useCallback(
    (watch = false) => {
      if (formChangedValues && Object.keys(formChangedValues).length > 0) {
        const user = getUser();
        if (!user) return false;

        // Transform current fields from formValues
        const currentFields = Object.keys(formChangedValues).reduce((acc, key) => {
          const typedKey = key as keyof typeof formChangedValues;
          if (typedKey === 'schedulingOpportunity') {
            if (callFormState[typedKey] === undefined) {
              acc[typedKey] = null;
            } else {
              acc[typedKey] =
                callFormState.schedulingOpportunity ===
                CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED;
            }
          } else if (typedKey === 'schedulingOutcome') {
            if (callFormState[typedKey] === undefined) {
              acc[typedKey] = null;
            } else {
              acc[typedKey] =
                callFormState.schedulingOutcome === CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED;
            }
          } else if (typedKey === 'callSentiment' && callFormState[typedKey] === undefined) {
            acc[typedKey] = null;
          } else {
            acc[typedKey] = callFormState[typedKey];
          }
          return acc;
        }, {} as Partial<Record<keyof typeof formChangedValues, any>>);

        // Transform edited fields from parent values
        const editedFields = Object.keys(formChangedValues).reduce((acc, key) => {
          const typedKey = key as keyof typeof formChangedValues;
          if (typedKey === 'schedulingOpportunity') {
            acc[typedKey] =
              formValues.schedulingOpportunity ===
              CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED;
          } else if (typedKey === 'schedulingOutcome') {
            acc[typedKey] =
              formValues.schedulingOutcome === CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED;
          } else {
            acc[typedKey] = formValues[typedKey];
          }
          return acc;
        }, {} as Partial<Record<keyof typeof formChangedValues, any>>);

        !watch && setChangedFields(Object.keys(formChangedValues) as (keyof typeof callFormState)[]);

        return {
          id: call?.id ?? '',
          currentFields: currentFields,
          editedFields: editedFields,
          locationId: call?.locationId ?? '',
          userId: user?.userID,
        };
      }
      return false;
    },
    [call?.id, call?.locationId, callFormState, formChangedValues, formValues]
  );

  const updateEditedCallInList = useCallback(
    (updatedFields: Partial<CallIntelTypes.CallRecord>, isRestore = false) => {
      const updatedIsFieldEdited = !isRestore
        ? computeIsFieldEdited(
            call?.isFieldEdited as CallIntelTypes.IsFieldEdited,
            changedFields as (keyof CallIntelTypes.CallEditableFields)[]
          )
        : undefined;

      onUpdateCall({
        ...call,
        ...updatedFields,
        ...(!isRestore && { isFieldEdited: updatedIsFieldEdited }),
      } as CallIntelTypes.CallRecord);

      if (isRestore) {
        setIsRestored(true);
      }
    },
    [call, changedFields, onUpdateCall]
  );

  const handleFieldSave = useCallback(() => {
    const payload = computeChangedPayload();
    if (payload) {
      editAIOutputMutate(payload);
    } else {
      setIsEditMode(false);
      setCorrectionBannerConfig((prev) => ({
        ...prev,
        canMakeCorrection: true,
      }));
    }
    setUnsavedEditChangesConfig({ ...unsavedEditChangesConfig, hasUnsavedChanges: false, actionType: 'NO_ACTION' });
  }, [computeChangedPayload, editAIOutputMutate, setUnsavedEditChangesConfig]);

  // Sync callFormState with initialFormState when initialFormState changes
  useEffect(() => {
    setCallFormState(initialFormState);
  }, [initialFormState]);

  useEffect(() => {
    if (pendingUpdateCallInList) {
      updateEditedCallInList(pendingUpdateCallInList);
      setPendingUpdateCallInList(null);
      setChangedFields(null);
    }
    //due to infinite loop issue with useEffect, we are not using updateEditedCallInList directly in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUpdateCallInList]);

  useEffect(() => {
    if (isRestored && initialFormState) {
      setCallFormState(initialFormState);
      resetForm();
      setIsRestored(false);
    }
  }, [isRestored, initialFormState, resetForm]);

  useEffect(() => {
    /**This effect is used to determine if form values changed in edit mode */
    if (isEditMode && formValues) {
      const isChanged = computeChangedPayload(true);
      setIsFormLevelSaveDisabled(!isChanged);
    }
  }, [isEditMode, formValues, computeChangedPayload]);

  useEffect(() => {
    if (!unsavedEditChangesConfig.dontAskAgain && isEditMode && unsavedEditChangesConfig.actionType !== 'SHOW_MODAL') {
      setUnsavedEditChangesConfig({ ...unsavedEditChangesConfig, hasUnsavedChanges: !isFormLevelSaveDisabled });
    }
  }, [isFormLevelSaveDisabled, unsavedEditChangesConfig.dontAskAgain, unsavedEditChangesConfig.actionType, isEditMode]);

  useEffect(() => {
    const { actionType, hasUnsavedChanges } = unsavedEditChangesConfig;
    const handleDiscardChanges = (reset = true) => {
      reset && resetForm();
      setUnsavedEditChangesConfig({ hasUnsavedChanges: !reset, actionType: 'NO_ACTION' });
    };

    if (actionType === 'SAVE' && hasUnsavedChanges && !isFormLevelSaveDisabled) {
      handleFieldSave();
    } else if (actionType === 'CLOSE' && hasUnsavedChanges && !isFormLevelSaveDisabled) {
      handleDiscardChanges(false);
    } else if (actionType === 'DISCARD' && hasUnsavedChanges && !isFormLevelSaveDisabled) {
      handleDiscardChanges();
    }
    //due to infinite loop issue with useEffect, we are not updating the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsavedEditChangesConfig]);

  useEffect(() => {
    if (pendingFieldSave && formChangedValues?.[pendingFieldSave]) {
      handleFieldSave();
      setPendingFieldSave(null);
    }
  }, [pendingFieldSave]);

  if (isProcessingCall || isLoading) {
    return <AnalyzingCallLoader lottie={analyzingCallLottie} message={t('Processing phone call...')} />;
  }

  if (!call || isFailedCall || isSkippedCall) {
    return (
      <div data-testid='call-analysis'>
        <CallEmptyState
          mainText={t('No Call Analysis Available')}
          style={{
            overflow: 'auto',
          }}
          svg={NoTranscription}
          subText={t('Please review the call recording.')}
        />
      </div>
    );
  }

  return (
    <div css={[styles.list, containerStyles]} data-testid='call-analysis'>
      {showCorrectionBanner && !!call && (
        <EditBanner
          call={call}
          callFlags={callFlags}
          isFormEdited={isFormEdited}
          isDisabled={isPrivateCall}
          correctionBannerConfig={correctionBannerConfig}
          onMakeCorrection={initiateCorrectionMode}
          onUpdateCall={updateEditedCallInList}
        />
      )}

      <NakedUl css={[styles.list, containerStyles]}>
        <li>
          <ContactTypeFormField
            call={call}
            callFlags={callFlags}
            callFormState={callFormState}
            isDemoAccount={isDemoAccount}
            isEditMode={isEditMode}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
            {...getFieldProps('contactType')}
          />
        </li>

        <li>
          <CallSentimentFormField
            call={call}
            callFlags={callFlags}
            callFormState={callFormState}
            isDemoAccount={isDemoAccount}
            isEditMode={isEditMode}
            {...getFieldProps('callSentiment')}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
          />
        </li>

        {isServiceQualityEnabled && hasServiceQualityAccess && (
          <li>
            <ServiceIssuesFormField
              call={call}
              callFlags={callFlags}
              callFormState={callFormState}
              isDemoAccount={isDemoAccount}
              isEditMode={isEditMode}
              {...getFieldProps('serviceQualityFlags')}
              unsavedEditChangesConfig={unsavedEditChangesConfig}
              setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
            />
          </li>
        )}

        <li>
          <SchedulingOutcomeV2Field
            call={call}
            callFlags={callFlags}
            callFormState={callFormState}
            isDemoAccount={isDemoAccount}
            isEditMode={isEditMode}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
            schedulingOutcomeV2FieldProps={getFieldProps('schedulingOutcomeV2')}
            onTriggerAutoSave={setPendingFieldSave}
          />
        </li>

        <li>
          <CategoryFormField
            call={call}
            callFlags={callFlags}
            callFormState={callFormState}
            isDemoAccount={isDemoAccount}
            isEditMode={isEditMode}
            {...getFieldProps('categories')}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
          />
        </li>

        <li>
          <AppointmentTypeFormField
            call={call}
            callFlags={callFlags}
            callFormState={callFormState}
            isDemoAccount={isDemoAccount}
            isEditMode={isEditMode}
            {...getFieldProps('appointmentTypes')}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
          />
        </li>
      </NakedUl>

      {isEditMode && (
        <ActionButtons
          isEditMode={isEditMode}
          cancelEditMode={cancelEditMode}
          handleFieldSave={handleFieldSave}
          isFormLevelSaveDisabled={isFormLevelSaveDisabled}
          isFormComplete={isFormComplete}
          isLoading={isAIOutputMutating}
          isDemoAccount={isDemoAccount}
        />
      )}
    </div>
  );
};

const styles = {
  list: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    overflow: auto;
  `,
};
